import { useEffect, useState } from 'react';
import type { AuftragAnsicht, Gesellschaft } from '@arbeitsrapporte/shared';
import { api } from '../api';
import { Fehler } from './Meldung';

export type Auftragskopf = Omit<AuftragAnsicht, 'positionen'>;

/**
 * Ein offener Auftrag aus dem Spiegel – gesucht, dann angetippt.
 *
 * Ein Baustein für beide Stellen, an denen ein Auftrag gewählt wird: der
 * neue Rapport und der geplante Einsatz. Vorher waren es zwei Bilder für
 * dieselbe Sache – Karten hier, ein Auswahlfeld mit Suche daneben dort.
 *
 * Die Liste zeigt die neuesten Nummern, und die Nummernkreise der beiden
 * Gesellschaften liegen nebeneinander: Ohne Schnitt standen dort nur
 * CH-Aufträge, und es sah aus, als gäbe es keine deutschen.
 */
export function Auftragswahl({
  onWahl,
  gesperrt = false,
  autoFocus = false,
}: {
  onWahl: (auftrag: Auftragskopf) => void;
  /** Solange etwas läuft – kein zweiter Griff. */
  gesperrt?: boolean;
  autoFocus?: boolean;
}) {
  const [suche, setSuche] = useState('');
  const [gesellschaft, setGesellschaft] = useState<Gesellschaft | ''>('');
  const [treffer, setTreffer] = useState<Auftragskopf[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => {
    let veraltet = false;
    const t = setTimeout(() => {
      api
        .auftraege(suche, gesellschaft)
        .then((a) => {
          if (!veraltet) setTreffer(a);
        })
        .catch((f: unknown) => {
          if (!veraltet) setFehler(f);
        });
    }, 200);

    return () => {
      veraltet = true;
      clearTimeout(t);
    };
  }, [suche, gesellschaft]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-60 flex-1">
          <label className="feldname" htmlFor="auftragsuche">
            Suche
          </label>
          <input
            autoFocus={autoFocus}
            id="auftragsuche"
            className="feld"
            placeholder="Auftragsnummer, Bezeichnung oder Kunde"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
          />
        </div>

        <div className="w-32">
          <label className="feldname" htmlFor="auftrag-gesellschaft">
            Gesellschaft
          </label>
          <select
            id="auftrag-gesellschaft"
            className="feld"
            value={gesellschaft}
            onChange={(e) => setGesellschaft(e.target.value as Gesellschaft | '')}
          >
            <option value="">beide</option>
            <option value="CH">CH</option>
            <option value="DE">DE</option>
          </select>
        </div>
      </div>

      <Fehler fehler={fehler} />

      {treffer === null && !fehler && <p className="text-slate-600">Lädt …</p>}
      {treffer?.length === 0 && <p className="text-slate-600">Kein offener Auftrag passt.</p>}

      <ul className="space-y-2">
        {treffer?.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              disabled={gesperrt}
              onClick={() => onWahl(a)}
              className="karte block w-full text-left hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Auftragszeile auftrag={a} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Nummer, Gesellschaft, Bezeichnung, Kunde – so, wie ein Auftrag überall erscheint. */
export function Auftragszeile({ auftrag: a }: { auftrag: Auftragskopf }) {
  return (
    <>
      <div className="flex items-center gap-3">
        <span className="font-semibold text-slate-900">{a.nummer}</span>
        <span className="ml-auto shrink-0 rounded border border-slate-300 px-2 py-0.5 text-xs tracking-wide text-slate-500 uppercase">
          {a.gesellschaft}
        </span>
      </div>
      <div className="mt-1 text-slate-700">{a.bezeichnung}</div>
      <div className="mt-0.5 text-sm text-slate-500">
        {a.kundeName || '– kein Kunde zugeordnet –'}
      </div>
    </>
  );
}
